/**
 * Detalle de un **activo** con sus sub-activos.
 *
 * Puerto de `views/maintenance/assets/view.php`: los datos del equipo y la lista
 * de componentes con su consumo frente al umbral de galonaje, que es lo que
 * decide si toca mantenimiento.
 */

import { Boxes, CalendarClock, Gauge, ShieldAlert } from 'lucide-react';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Dialog } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatNumber } from '@/lib/utils';

import { useActivoDetalle } from '../activos.api';
import { varianteEstado, type SubActivo } from '../sub-activos.types';

/** Color de la barra de consumo según el estado calculado. */
const BARRA: Record<string, string> = {
  danger: 'bg-rose-500',
  warning: 'bg-amber-500',
  success: 'bg-emerald-500',
  neutral: 'bg-ink-300',
};

/** Dato de la ficha del activo. */
function Dato({ etiqueta, valor }: { etiqueta: string; valor: string }) {
  return (
    <div>
      <dt className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">{etiqueta}</dt>
      <dd className="text-sm text-ink-800">{valor || '—'}</dd>
    </div>
  );
}

/** Fila de un sub-activo con su barra de consumo. */
function FilaSubActivo({ subActivo }: { subActivo: SubActivo }) {
  return (
    <tr className="border-b border-ink-50 last:border-0">
      <td className="py-3">
        <span className="block font-semibold text-ink-900">{subActivo.nombre}</span>
        <span className="block font-mono text-[11px] text-ink-400">
          {subActivo.codigo}
          {subActivo.tipo_componente ? ` · ${subActivo.tipo_componente}` : ''}
        </span>
      </td>
      <td className="py-3 text-right tabular-nums text-ink-700">
        {formatNumber(subActivo.usado)}
        <span className="block text-[11px] text-ink-400">
          de {formatNumber(subActivo.umbral)}
        </span>
      </td>
      <td className="py-3">
        <div className="flex min-w-32 flex-col gap-1">
          <div className="h-2 w-full overflow-hidden rounded-full bg-ink-100">
            <div
              className={cn('h-full rounded-full', BARRA[subActivo.estado_color] ?? 'bg-ink-300')}
              style={{ width: `${subActivo.progreso}%` }}
            />
          </div>
          <span className="text-[11px] text-ink-400">
            {subActivo.progreso}% · quedan {formatNumber(subActivo.restante)}
          </span>
        </div>
      </td>
      <td className="py-3 text-right">
        <Badge variant={varianteEstado(subActivo.estado_color)}>
          {subActivo.estado_etiqueta}
        </Badge>
      </td>
    </tr>
  );
}

export function ActivoDetalle({
  activoId,
  onCerrar,
}: {
  activoId: number;
  onCerrar: () => void;
}) {
  const detalle = useActivoDetalle(activoId);
  const activo = detalle.data?.activo;
  const subActivos = detalle.data?.sub_activos ?? [];
  const criticos = subActivos.filter((subActivo) => subActivo.estado_mantenimiento === 'CRITICO');

  return (
    <Dialog
      open
      onClose={onCerrar}
      size="xl"
      title={activo ? `${activo.nombre}` : 'Detalle del activo'}
      description={
        activo ? `${activo.categoria} · ${activo.codigo}` : 'Cargando el detalle del activo…'
      }
      footer={
        <Badge variant="outline">
          <Boxes className="size-3" aria-hidden="true" />
          {subActivos.length} sub-activos
        </Badge>
      }
    >
      {detalle.isLoading ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      ) : detalle.error ? (
        <Alert variant="error" title="No se pudo leer el activo">
          {detalle.error instanceof ApiError
            ? detalle.error.message
            : 'La API no respondió.'}
        </Alert>
      ) : activo ? (
        <div className="flex flex-col gap-5">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={activo.activo ? 'success' : 'neutral'}>
              {activo.activo ? 'Activo' : 'En desuso'}
            </Badge>
            {activo.sub_activos_activos > 0 ? (
              <Badge variant="brand">
                <Gauge className="size-3" aria-hidden="true" />
                {activo.sub_activos_activos} componentes vigilados
              </Badge>
            ) : null}
            {criticos.length > 0 ? (
              <Badge variant="danger">
                <ShieldAlert className="size-3" aria-hidden="true" />
                {criticos.length} en crítico
              </Badge>
            ) : null}
          </div>

          <dl className="grid gap-4 sm:grid-cols-3">
            <Dato etiqueta="Número de serie" valor={activo.serie} />
            <Dato etiqueta="Modelo" valor={activo.modelo} />
            <Dato etiqueta="Ubicación" valor={activo.ubicacion} />
            <Dato etiqueta="Instalación" valor={activo.instalacion ?? ''} />
            <Dato etiqueta="Garantía hasta" valor={activo.garantia ?? ''} />
            <Dato etiqueta="Registrado" valor={activo.creado ?? ''} />
          </dl>

          {activo.descripcion ? (
            <p className="rounded-card border border-ink-100 bg-ink-50/60 p-3 text-sm text-ink-600">
              {activo.descripcion}
            </p>
          ) : null}

          <section className="flex flex-col gap-2">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-ink-900">
              <CalendarClock className="size-4 text-brand-500" aria-hidden="true" />
              Sub-activos y consumo
            </h3>

            {subActivos.length === 0 ? (
              <Alert variant="info" title="Sin sub-activos">
                Este activo todavía no tiene componentes registrados.
              </Alert>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-ink-100 text-xs font-semibold uppercase tracking-wider text-ink-400">
                      <th className="py-2">Componente</th>
                      <th className="py-2 text-right">Consumido</th>
                      <th className="py-2">Avance del umbral</th>
                      <th className="py-2 text-right">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subActivos.map((subActivo) => (
                      <FilaSubActivo key={subActivo.id} subActivo={subActivo} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      ) : null}
    </Dialog>
  );
}
